import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Check, ImagePlus, Search, ArrowUp, ArrowDown, Plus, Trash2, Film } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, mediaUrl, errMsg } from "@/lib/api";

export const inCls = "w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--ib-orange)]";
export const btn = "inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50";
export const btnPrimary = `${btn} bg-[color:var(--ib-orange)] text-white hover:bg-[color:var(--ib-orange-dark)]`;
export const btnGhost = `${btn} border border-black/15 bg-white hover:border-black/40`;

export function PageTitle({ title, sub, children }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div><h1 className="font-display text-3xl uppercase" data-testid="admin-page-title">{title}</h1>{sub && <p className="mt-1 max-w-2xl text-sm text-black/60">{sub}</p>}</div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

const STATUS_COLORS = { published: "bg-green-100 text-green-800", approved: "bg-green-100 text-green-800", active: "bg-green-100 text-green-800", draft: "bg-amber-100 text-amber-800", pending: "bg-amber-100 text-amber-800", inactive: "bg-gray-200 text-gray-700", archived: "bg-gray-200 text-gray-700", New: "bg-orange-100 text-orange-800", Won: "bg-green-100 text-green-800", Lost: "bg-gray-200 text-gray-700" };
export function Badge({ s }) {
  return <span className={`inline-block rounded-sm px-2 py-0.5 text-[0.7rem] font-bold uppercase tracking-wider ${STATUS_COLORS[s] || "bg-blue-50 text-blue-800"}`}>{s}</span>;
}

export function useUploader() {
  const [busy, setBusy] = useState(false);
  const upload = async (files, meta = {}) => {
    if (!files?.length) return [];
    setBusy(true);
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    fd.append("meta", JSON.stringify(meta));
    try {
      const { data } = await api.post("/admin/media/upload", fd);
      toast.success(`${data.length} file(s) uploaded`);
      return data;
    } catch (e) {
      toast.error(errMsg(e));
      return [];
    } finally {
      setBusy(false);
    }
  };
  return { busy, upload };
}

export function DropZone({ onFiles, busy, accept = "image/*,video/*", label = "Drag & drop photos or videos, or click to browse", testid = "media-dropzone" }) {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  return (
    <div onClick={() => ref.current?.click()} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onFiles(e.dataTransfer.files); }}
      className={`flex cursor-pointer flex-col items-center gap-2 border-2 border-dashed p-6 text-center text-sm transition-colors ${over ? "border-[color:var(--ib-orange)] bg-orange-50" : "border-black/15 bg-white hover:border-black/30"}`} data-testid={testid}>
      <Upload size={22} className="text-[color:var(--ib-orange)]" />
      {busy ? "Uploading… please wait" : label}
      <input ref={ref} type="file" multiple accept={accept} hidden onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} data-testid={`${testid}-input`} />
    </div>
  );
}

export function Thumb({ url, kind = "image", className = "" }) {
  if (kind === "video") return <div className={`flex items-center justify-center bg-black text-white ${className}`}><Film size={22} /></div>;
  return <img src={mediaUrl(url, "sm")} alt="" loading="lazy" className={`object-cover ${className}`} />;
}

export function MediaPicker({ open, onClose, onPick, multiple = false, kind = "image" }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const { busy, upload } = useUploader();
  const load = () => api.get("/admin/media", { params: { kind, q } }).then((r) => setItems(r.data));
  useEffect(() => { if (open) { setSel([]); load(); } }, [open, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (u) => (multiple ? setSel((s) => (s.includes(u) ? s.filter((x) => x !== u) : [...s, u])) : onPick(u));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto" data-testid="media-picker">
        <DialogHeader><DialogTitle>Choose from media library</DialogTitle></DialogHeader>
        <div className="flex gap-3">
          <div className="relative flex-1"><Search size={16} className="absolute left-3 top-2.5 text-black/40" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by file name or tag" className={`${inCls} pl-9`} data-testid="media-picker-search" /></div>
        </div>
        <DropZone busy={busy} accept={kind === "video" ? "video/*" : "image/*"} onFiles={async (f) => { const up = await upload(f); if (up.length) load(); }} testid="media-picker-dropzone" />
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 md:grid-cols-6">
          {items.map((m) => (
            <button key={m.id} type="button" onClick={() => toggle(m.url)} className={`relative aspect-square overflow-hidden border-2 ${sel.includes(m.url) ? "border-[color:var(--ib-orange)]" : "border-transparent"}`} data-testid={`media-picker-item-${m.id}`}>
              <Thumb url={m.url} kind={m.kind} className="h-full w-full" />
              {sel.includes(m.url) && <span className="absolute right-1 top-1 rounded-full bg-[color:var(--ib-orange)] p-0.5 text-white"><Check size={14} /></span>}
            </button>
          ))}
        </div>
        {multiple && <div className="flex justify-end"><button className={btnPrimary} onClick={() => onPick(sel)} disabled={!sel.length} data-testid="media-picker-confirm">Add {sel.length} selected</button></div>}
      </DialogContent>
    </Dialog>
  );
}

function ImageField({ value, onChange, testid, kind = "image" }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center gap-3">
      {value ? <Thumb url={value} kind={kind} className="h-20 w-28 border" /> : <div className="flex h-20 w-28 items-center justify-center border border-dashed text-xs text-black/40">None</div>}
      <div className="flex flex-col gap-1">
        <button type="button" className={btnGhost} onClick={() => setOpen(true)} data-testid={`${testid}-choose`}><ImagePlus size={14} /> {value ? "Change" : "Choose"}</button>
        {value && <button type="button" className="text-left text-xs text-red-600" onClick={() => onChange("")}>Remove</button>}
      </div>
      <MediaPicker open={open} kind={kind} onClose={() => setOpen(false)} onPick={(u) => { onChange(u); setOpen(false); }} />
    </div>
  );
}

function ImagesField({ value = [], onChange, testid }) {
  const [open, setOpen] = useState(false);
  const move = (i, d) => { const c = [...value]; const j = i + d; if (j < 0 || j >= c.length) return; [c[i], c[j]] = [c[j], c[i]]; onChange(c); };
  return (
    <div>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-6">
        {value.map((u, i) => (
          <div key={u + i} className="group relative aspect-square overflow-hidden border">
            <Thumb url={u} className="h-full w-full" />
            {i === 0 && <span className="absolute left-1 top-1 bg-[color:var(--ib-orange)] px-1 text-[0.6rem] font-bold uppercase text-white">First</span>}
            <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100">
              <button type="button" onClick={() => move(i, -1)} aria-label="Move left"><ArrowUp size={14} className="-rotate-90" /></button>
              <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label="Remove"><X size={14} /></button>
              <button type="button" onClick={() => move(i, 1)} aria-label="Move right"><ArrowDown size={14} className="-rotate-90" /></button>
            </div>
          </div>
        ))}
        <button type="button" onClick={() => setOpen(true)} className="flex aspect-square flex-col items-center justify-center gap-1 border border-dashed text-xs text-black/50 hover:border-[color:var(--ib-orange)]" data-testid={`${testid}-add`}><ImagePlus size={18} />Add photos</button>
      </div>
      <MediaPicker open={open} multiple onClose={() => setOpen(false)} onPick={(urls) => { onChange([...value, ...urls.filter((u) => !value.includes(u))]); setOpen(false); }} />
    </div>
  );
}

const registry = {};

function PairsField({ value = [], onChange, fields, testid }) {
  const Nested = registry.Fields;
  return (
    <div className="space-y-3">
      {value.map((row, i) => (
        <div key={i} className="relative border border-black/10 bg-[color:var(--ib-off)] p-3 pr-10">
          <Nested schema={fields} value={row} onChange={(r) => onChange(value.map((x, j) => (j === i ? r : x)))} testid={`${testid}-${i}`} compact />
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="absolute right-2 top-2 text-red-600" aria-label="Remove row"><Trash2 size={16} /></button>
        </div>
      ))}
      <button type="button" className={btnGhost} onClick={() => onChange([...value, {}])} data-testid={`${testid}-add-row`}><Plus size={14} /> Add</button>
    </div>
  );
}

function Input({ f, v, set, testid }) {
  const opts = (f.options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  switch (f.type) {
    case "textarea": return <textarea rows={f.rows || 4} value={v || ""} onChange={(e) => set(e.target.value)} className={inCls} data-testid={testid} />;
    case "number": return <input type="number" value={v ?? ""} onChange={(e) => set(e.target.value === "" ? null : Number(e.target.value))} className={inCls} data-testid={testid} />;
    case "switch": return <Switch checked={!!v} onCheckedChange={set} data-testid={testid} />;
    case "select": return <select value={v ?? ""} onChange={(e) => set(e.target.value)} className={inCls} data-testid={testid}>{!f.noEmpty && <option value="">—</option>}{opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>;
    case "image": return <ImageField value={v} onChange={set} testid={testid} />;
    case "video": return <ImageField value={v} onChange={set} testid={testid} kind="video" />;
    case "images": return <ImagesField value={v || []} onChange={set} testid={testid} />;
    case "list": return <textarea rows={f.rows || 4} value={(v || []).join("\n")} onChange={(e) => set(e.target.value.split("\n"))} onBlur={(e) => set(e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} className={inCls} placeholder="One per line" data-testid={testid} />;
    case "pairs": return <PairsField value={v || []} onChange={set} fields={f.fields} testid={testid} />;
    default: return <input value={v || ""} onChange={(e) => set(e.target.value)} className={inCls} data-testid={testid} />;
  }
}

export function Fields(props) {
  return <FieldGrid {...props} />;
}
registry.Fields = Fields;

function FieldGrid({ schema, value, onChange, testid = "field", compact = false }) {
  return (
    <div className={`grid gap-4 ${compact ? "sm:grid-cols-2" : "md:grid-cols-2"}`}>
      {schema.map((f) => (
        <div key={f.key} className={f.full || ["textarea", "images", "pairs", "list"].includes(f.type) ? "md:col-span-2 sm:col-span-2" : ""}>
          <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-black/60">{f.label}</label>
          <Input f={f} v={value?.[f.key]} set={(v) => onChange({ ...value, [f.key]: v })} testid={`${testid}-${f.key}`} />
          {f.help && <p className="mt-1 text-xs text-black/50">{f.help}</p>}
        </div>
      ))}
    </div>
  );
}
